import { Box, Divider, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { createPortal } from 'react-dom';
import type { QuotePdfModel } from './quotePdfModel';

export const QUOTE_PDF_ROOT_ID = 'quote-pdf-root';

/**
 * Regras de impressão: na tela o documento fica oculto; no `print` esconde-se o
 * resto do `body` e só o documento é paginado, em A4 sem margens do browser
 * (as margens reais são o padding de cada página).
 */
const printStyles = `
#${QUOTE_PDF_ROOT_ID} { display: none; }
@media print {
  @page { size: A4; margin: 0; }
  body > *:not(#${QUOTE_PDF_ROOT_ID}) { display: none !important; }
  #${QUOTE_PDF_ROOT_ID} { display: block !important; }
  #${QUOTE_PDF_ROOT_ID} .quote-pdf-page { break-after: page; }
  #${QUOTE_PDF_ROOT_ID} .quote-pdf-page:last-child { break-after: auto; }
  /* Detalhamento longo continua na folha seguinte: repete o padding da página em cada fragmento. */
  #${QUOTE_PDF_ROOT_ID} .quote-pdf-page--flow { -webkit-box-decoration-break: clone; box-decoration-break: clone; }
}
/* Um card de cobertura nunca é partido entre duas folhas (antes «Cobertura de responsabilidade civil» saía cortado). */
#${QUOTE_PDF_ROOT_ID} .quote-pdf-cover-card { break-inside: avoid; page-break-inside: avoid; }
`;

/** Documento sempre em claro: é impresso em papel, independente do tema da app. */
const INK = '#121212';
const MUTED = '#6b6e76';
const LINE = '#dddee1';
const BRAND = '#7f27df';

function PageChrome({
  model,
  pageNumber,
  totalPages,
  flow = false,
  children
}: {
  model: QuotePdfModel;
  pageNumber: number;
  totalPages: number;
  /** Página de conteúdo variável: cresce além de uma folha A4 em vez de transbordar por cima da quebra. */
  flow?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      className={flow ? 'quote-pdf-page quote-pdf-page--flow' : 'quote-pdf-page'}
      sx={{
        bgcolor: '#ffffff',
        boxSizing: 'border-box',
        color: INK,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Poppins, sans-serif',
        ...(flow ? { minHeight: '297mm' } : { height: '297mm' }),
        mx: 'auto',
        p: '14mm',
        width: '210mm'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '6mm' }}>
        <Box>
          <Typography sx={{ color: INK, fontSize: 20, fontWeight: 600 }}>Cotação de seguro</Typography>
          {model.validUntil ? (
            <Typography sx={{ color: MUTED, fontSize: 12 }}>Válida até: {model.validUntil}</Typography>
          ) : null}
        </Box>
      </Box>
      {model.clubName || model.sellerName ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '6mm' }}>
          <Typography sx={{ color: MUTED, fontSize: 11, fontWeight: 500 }}>{model.clubName ?? ''}</Typography>
          {model.sellerName ? (
            <Typography sx={{ color: MUTED, fontSize: 11 }}>Vendedor: {model.sellerName}</Typography>
          ) : null}
        </Box>
      ) : null}
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
      <Divider sx={{ borderColor: LINE, mb: '3mm' }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ color: MUTED, fontSize: 10 }}>
          {model.quoteId ? `Cotação ${model.quoteId}` : 'Cotação'}
          {model.issuedAt ? ` · Emitida em ${model.issuedAt}` : ''}
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: 10 }}>
          Página {pageNumber} de {totalPages}
        </Typography>
      </Box>
    </Box>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <Typography sx={{ color: INK, fontSize: 15, fontWeight: 600, mb: '2mm' }}>{children}</Typography>;
}

/**
 * PDF da cotação (Figma 1700:48 e 1700:132) — duas páginas A4 renderizadas fora do
 * ecrã e materializadas pelo diálogo de impressão do browser ("Guardar como PDF").
 *
 * Optou-se por impressão nativa em vez de uma biblioteca de PDF para não acrescentar
 * dependência nem duplicar a tipografia/tokens num canvas.
 */
export default function QuotePdfDocument({ model }: { model: QuotePdfModel }) {
  const [host, setHost] = useState<HTMLElement | null>(null);

  // O container vive fora da árvore da app para que a regra de impressão
  // `body > *:not(#quote-pdf-root)` consiga esconder o resto da página.
  useEffect(() => {
    let node = document.getElementById(QUOTE_PDF_ROOT_ID);
    let created = false;
    if (!node) {
      node = document.createElement('div');
      node.id = QUOTE_PDF_ROOT_ID;
      document.body.appendChild(node);
      created = true;
    }
    setHost(node);
    return () => {
      if (created && node?.parentNode) node.parentNode.removeChild(node);
    };
  }, []);

  if (!host) return null;

  const totalPages = model.coverages.length > 0 ? 2 : 1;

  return createPortal(
    <>
      <style>{printStyles}</style>
      <PageChrome model={model} pageNumber={1} totalPages={totalPages}>
        <SectionTitle>Cliente</SectionTitle>
        <Box sx={{ display: 'flex', gap: '4mm', mb: '2mm' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{model.customerName}</Typography>
          {model.customerDocument ? (
            <Typography sx={{ color: MUTED, fontSize: 12 }}>CPF: {model.customerDocument}</Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: '4mm', mb: '8mm' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{model.equipmentLabel}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>{model.equipmentValue}</Typography>
        </Box>

        <SectionTitle>Plano selecionado</SectionTitle>
        {model.planRows.map((row) => (
          <Box key={row.label} sx={{ mb: '3mm' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 13 }}>{row.label}</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{row.value}</Typography>
            </Box>
            {row.details?.map((detail) => (
              <Typography key={detail} sx={{ color: MUTED, fontSize: 11 }}>
                {detail}
              </Typography>
            ))}
          </Box>
        ))}
        {model.operationalCost ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '3mm' }}>
            <Typography sx={{ fontSize: 13 }}>Custos operacionais</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{model.operationalCost}</Typography>
          </Box>
        ) : null}
        <Divider sx={{ borderColor: LINE, mb: '2mm' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '3mm' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Total mensal</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{model.monthlyTotal}</Typography>
        </Box>
        {model.membershipFee ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '8mm' }}>
            <Typography sx={{ fontSize: 13 }}>Taxa de adesão</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{model.membershipFee} (Taxa única)</Typography>
          </Box>
        ) : null}

        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: '8px', display: 'flex', gap: '6mm', p: '6mm' }}>
          <Box sx={{ flexShrink: 0, height: 96, width: 96 }}>
            <QRCode size={96} style={{ height: 96, width: 96 }} value={model.resumeUrl} />
          </Box>
          <Box>
            <Typography sx={{ color: BRAND, fontSize: 15, fontWeight: 600, mb: '2mm' }}>
              Finalize sua contratação
            </Typography>
            <Typography sx={{ fontSize: 12, mb: '2mm' }}>
              Escaneie o QR Code ou acesse o link abaixo para retomar esta cotação e concluir a compra.
            </Typography>
            <Typography sx={{ fontSize: 11, wordBreak: 'break-all' }}>{model.resumeUrl}</Typography>
            {model.validUntil ? (
              <Typography sx={{ color: MUTED, fontSize: 10, mt: '2mm' }}>
                Link válido até {model.validUntil}.
              </Typography>
            ) : null}
          </Box>
        </Box>
      </PageChrome>

      {model.coverages.length > 0 ? (
        <PageChrome flow model={model} pageNumber={2} totalPages={totalPages}>
          <SectionTitle>Cliente (identificação)</SectionTitle>
          <Typography sx={{ color: MUTED, fontSize: 12, mb: '6mm' }}>
            {model.customerName}
            {model.customerDocument ? ` · CPF: ${model.customerDocument}` : ''}
          </Typography>
          <SectionTitle>Detalhamento da cobertura</SectionTitle>
          {model.coverages.map((coverage) =>
            coverage.covers.map((cover) => (
              <Box
                className="quote-pdf-cover-card"
                key={`${coverage.productName}-${cover.name}`}
                sx={{ border: `1px solid ${LINE}`, borderRadius: '8px', mb: '3mm', p: '4mm' }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: '1mm' }}>{cover.name}</Typography>
                <Typography sx={{ fontSize: 11 }}>
                  <strong>Detalhes do serviço:</strong> {cover.description}
                </Typography>
              </Box>
            ))
          )}
        </PageChrome>
      ) : null}
    </>,
    host
  );
}
